import { useState } from 'react';
import { Avatar, Drawer } from 'antd';

import { CloseIcon } from '../../../../../components/icons';
import Button from '../../../../../components/button/Button';
import CustomTextField from '../../../../../components/CustomTextInput';
import { getErrorMessage, openNotification } from '../../../../../utils/helper';
import { useNavigate } from 'react-router-dom';
import { mutateConfig } from '../../../../../api/queryBase';
import { AxiosError } from 'axios';
import { TreasuryAccount } from '../../../../../interfaces/Treasury';
import { useBankLogos } from '../../../../../customhooks/useBankLogos';
import { useMediaQuery, useTheme } from '@mui/material';

const mutation = mutateConfig({ method: 'delete' });

export const UnlinkAccount = ({
    open,
    onClose,
    selected
}: {
    open: boolean;
    onClose: () => void;
    selected?: TreasuryAccount | null;
}) => {
    const navigate = useNavigate();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [text, setText] = useState('');

    const { getBankByCode } = useBankLogos();

    const { mutate, isLoading } = mutation({
        url: `/banks/connect/${selected?.id}`,
        cacheKeys: 'treasury-accounts'
    });

    const handleSubmit = async () => {
        mutate(
            {},
            {
                onSettled: (res: any, error: AxiosError | undefined | any) => {
                    if (error) {
                        openNotification(
                            'error',
                            getErrorMessage(error) || 'Something went wrong'
                        );
                    } else {
                        openNotification(
                            'success',
                            'Bank account disconnect request successful'
                        );
                        onClose();
                        navigate('/treasury/overview/accounts');
                    }
                }
            }
        );
    };

    const handleClose = () => {
        setText('');
        onClose();
    };

    return (
        <Drawer
            placement={isMobile ? 'bottom' : 'right'}
            closable={false}
            onClose={handleClose}
            open={open}
            width={isMobile ? undefined : 400}
            height={isMobile ? 'auto' : undefined}
            style={isMobile ? { maxHeight: '90vh' } : {}}
        >
            <div className='mt-[56px]  px-6 lg:w-[400px] pb-[40px]'>
                <div className='flex justify-between items-center mb-5'>
                    <h4 className='text-ebony text-[18px] font-semibold leading-[28px]'>
                        Unlink account
                    </h4>
                    <button onClick={handleClose}>
                        <CloseIcon />
                    </button>
                </div>

                <p className='text-black font-medium text-[14px] leading-[20px] mb-6'>
                    By clicking “Unlink account”, this bank account will be
                    removed from FrontEdge, and we will no longer have access to
                    its data.
                </p>

                <div className='border border-gray300 p-4 rounded-[4px] mb-[24px]'>
                    <div className='flex items-center gap-2'>
                        <Avatar
                            size={24}
                            src={getBankByCode(selected?.code || '')?.logo}
                            style={{ border: '1px solid #F2F4F7' }}
                        />

                        <p className='text-gray700 font-medium text-[14px] leading-[20px]'>
                            {selected?.name}
                        </p>
                    </div>

                    <p className='text-paleSky text-[14px] leading-[18px] py-[9px]'>
                        Acct No:{' '}
                        <span className='font-semibold'>
                            {selected?.accountNumber}
                        </span>
                    </p>

                    <p className='text-paleSky text-[14px] leading-[18px]'>
                        Acct Name:{' '}
                        <span className='font-semibold'>
                            {selected?.accountName}
                        </span>
                    </p>
                </div>

                <CustomTextField
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    label='To confirm, type “unlink” in the box below'
                />

                <Button
                    text='Unlink account'
                    className='w-full mt-[20px]'
                    onClick={handleSubmit}
                    variant='error'
                    disabled={text !== 'unlink'}
                    isLoading={isLoading}
                />
            </div>
        </Drawer>
    );
};
